/**
 * Turning a flat list of segments into the blocks an answer is laid out as.
 *
 * The old rule was a heuristic — three or more segments, every one of them
 * cited — and it was in direct contradiction with how segmentation works.
 * `to_segments` deliberately leaves a lead-in uncited, and the prompt tells the
 * model to lead with the answer, so every well-formed procedure failed the test
 * and rendered as one paragraph: fourteen steps of an End to End test arrived
 * as a wall of prose with superscripts scattered through it.
 *
 * The layout is now declared per segment by the server, so there is nothing
 * left to guess. Grouping lives here rather than in the screen because the
 * rendered answer and the copied answer must not be able to disagree about it.
 */

import type { AnswerSegment, Citation } from "../../lib/api";

export type Block =
  | { type: "heading"; items: AnswerSegment[] }
  | { type: "ordered"; items: AnswerSegment[] }
  | { type: "unordered"; items: AnswerSegment[] }
  | { type: "paragraph"; items: AnswerSegment[] };

function blockFor(segment: AnswerSegment): Block["type"] {
  switch (segment.kind) {
    case "heading":
      return "heading";
    case "step":
      return "ordered";
    case "bullet":
    case "key_value":
      // Both are unordered rows; a key-value row is a bullet whose label is set
      // off. Grouping them together is what lets one list mix "75 bar: RGB 219,
      // 220, 112" with a plain item and stay a single list.
      return "unordered";
    default:
      // Also the fallback for a segment with no kind at all — a message
      // replayed from a thread written before the field existed. Every such
      // segment becomes one paragraph block, which is character for character
      // what the screen rendered before this file existed.
      return "paragraph";
  }
}

/**
 * The one citation every item in a block shares, if they all share one.
 *
 * A list of eight colour bands read off one table carried the same chip eight
 * times. The repetition says nothing the first chip did not, and it competes
 * with the values, which are the thing being read.
 */
export function sharedCitation(block: Block): number | null {
  if (block.type === "paragraph" || block.items.length < 2) return null;
  const first = block.items[0].citation;
  if (first === null) return null;
  return block.items.every((item) => item.citation === first) ? first : null;
}

export function groupSegments(segments: AnswerSegment[]): Block[] {
  const blocks: Block[] = [];
  for (const segment of segments) {
    const type = blockFor(segment);
    const open = blocks[blocks.length - 1];
    // A heading is always its own block, so it closes the run above it and the
    // run below it starts fresh.
    if (open && open.type === type && type !== "heading") {
      open.items.push(segment);
      continue;
    }
    blocks.push({ type, items: [segment] } as Block);
  }
  return blocks;
}

/** ` [3]` — the citation chip, in a form that survives a paste into a textarea. */
const mark = (segment: AnswerSegment) =>
  segment.citation === null ? "" : ` [${segment.citation}]`;

/**
 * The answer as text, laid out the way it is on screen.
 *
 * Not `plain_text`. That string is the one the judge scores and it is
 * deliberately free of structure; this one is for a person pasting fourteen
 * steps into an incident log, where the numbers are the point.
 */
export function blocksToText(
  blocks: Block[],
  citations: Citation[] = [],
): string {
  const body = blocks
    .map((block) => {
      switch (block.type) {
        case "heading":
          return block.items[0].text;
        case "ordered":
          return block.items
            .map(
              (segment, index) =>
                `${segment.ordinal ?? index + 1}. ${segment.text}${mark(segment)}`,
            )
            .join("\n");
        case "unordered":
          // `-` rather than `•`: it survives a paste into Word, Teams, a ticket
          // field and a markdown file, and `•` does not always.
          return block.items
            .map((segment) => `- ${segment.text}${mark(segment)}`)
            .join("\n");
        default:
          return block.items
            .map((segment) => `${segment.text}${mark(segment)}`)
            .join(" ");
      }
    })
    .join("\n\n");

  if (citations.length === 0) return body;
  // The markers above are useless without this. An answer pasted into a log is
  // a record somebody may have to defend later, and a record that cannot be
  // traced back to a document and a page is the thing this system exists to
  // prevent.
  const sources = citations
    .map((c) => `[${c.n}] ${c.docNo} ${c.title} — ${c.locator}`)
    .join("\n");
  return `${body}\n\nSources:\n${sources}`;
}
